import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Drawer, Form, Input, InputNumber, Space } from 'antd';
import { createVehicleTypeSchema, updateVehicleTypeSchema, type VehicleTypeDto } from '@pams/shared';
import { api, type ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { applyServerIssues, useZodForm } from '@/lib/zodForm';

/**
 * Create or edit a vehicle type.
 *
 * ⚠️ THERE IS NO WILDCARD SWITCH ON THIS FORM, AND THAT IS NOT AN OMISSION.
 *
 * `isWildcard` is absent from both input schemas: the database permits exactly one
 * wildcard row (a partial unique index), so letting an administrator move the flag would
 * surface as an opaque E11000 in this drawer that they could not act on. The flag is
 * seeded and nothing else writes it — see `vehicleType.schema.ts`.
 */
export const VehicleTypeDrawer = ({
  open,
  mode,
  vehicleType,
  readOnly,
  onClose,
  onSuccess,
}: {
  open: boolean;
  mode: 'create' | 'edit';
  vehicleType?: VehicleTypeDto;
  readOnly: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const schema = mode === 'create' ? createVehicleTypeSchema : updateVehicleTypeSchema;
  const { form, field, submit, formErrors, setFormErrors } = useZodForm(schema);

  const save = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      mode === 'create'
        ? api.post<VehicleTypeDto>('/vehicle-types', values)
        : api.patch<VehicleTypeDto>(`/vehicle-types/${vehicleType?.id ?? ''}`, values),
    onSuccess: () => {
      void message.success(mode === 'create' ? 'Vehicle type created' : 'Vehicle type updated');
      void queryClient.invalidateQueries({ queryKey: queryKeys.vehicleTypes.all });
      onSuccess?.();
      onClose();
    },
    onError: (error: ApiError) => {
      setFormErrors(applyServerIssues(form, error.details).concat(error.details ? [] : [error.message]));
    },
  });

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={460}
      /* Opening a row without the write capability is VIEWING, and the title says so
         rather than promising an edit every control refuses. */
      title={
        mode === 'create' ? 'New vehicle type' : `${readOnly ? 'View' : 'Edit'} ${vehicleType?.name ?? 'vehicle type'}`
      }
      destroyOnHidden
      extra={
        <Space>
          {/* ⚠️ "Done", not "Close": the Drawer already ships a × whose accessible name IS
              "Close", and two controls with one name in a dialog is the same defect the
              row-action doctrine warns about. */}
          <Button onClick={onClose}>{readOnly ? 'Done' : 'Cancel'}</Button>
          {!readOnly && (
            <Button type="primary" loading={save.isPending} onClick={() => form.submit()}>
              Save
            </Button>
          )}
        </Space>
      }
    >
      {formErrors.length > 0 && <Alert type="error" showIcon className="mb-4" message={formErrors.join(' ')} />}

      <Form
        form={form}
        layout="vertical"
        disabled={readOnly}
        initialValues={{
          name: vehicleType?.name ?? '',
          code: vehicleType?.code ?? '',
          displayOrder: vehicleType?.displayOrder ?? 0,
        }}
        onFinish={(values: Record<string, unknown>) => {
          const parsed = submit(values);
          if (parsed.ok) save.mutate(parsed.data);
        }}
      >
        <Form.Item {...field('name')} label="Name">
          <Input placeholder="Police Control Room Van" />
        </Form.Item>

        <Form.Item {...field('code')} label="Code" help="What the requirement grid's column header shows. Uppercased.">
          <Input placeholder="PCRV" />
        </Form.Item>

        <Form.Item {...field('displayOrder')} label="Display order" help="Lower sorts first in the picker.">
          <InputNumber min={0} max={10_000} className="w-full" />
        </Form.Item>
      </Form>
    </Drawer>
  );
};
